import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SafeUrl } from '@angular/platform-browser';
import { MFGCompanyService } from '../../../../services/product/company.service';
import { CompanyLookupItem } from '../../../../services/product/company.model';
import { ProductsFileMgt } from '../../../../services/product/file-management/products.file-mgt.service';
import { LanguageService } from '../../../../services/language.service';
import { CompaniesIcon } from "../../../../shared/ui/icons/companies-icon/companies-icon";
import { ArrowNavIcon } from "../../../../shared/ui/icons/arrow-nav-icon/arrow-nav-icon";

export interface Company {
  id: number;
  titleEn: string;
  titleAr: string;
}

@Component({
  selector: 'app-companies-list',
  imports: [CompaniesIcon, ArrowNavIcon],
  templateUrl: './companies-list.html',
  styleUrl: './companies-list.scss',
})
export class CompaniesList implements OnInit {
  private router = inject(Router);
  private companyService = inject(MFGCompanyService);
  private fileMgt = inject(ProductsFileMgt);
  private languageService = inject(LanguageService);

  companies = signal<Company[]>([]);
  companyImages = new Map<number, { url: SafeUrl | string; isImage: boolean }>();
  errorMessage = signal<string>('');

  currentPage = signal<number>(1);
  pageSize = signal<number>(10);

  isArabic = computed(() => this.languageService.getCurrentLocale() === 'ar');

  paginatedCompanies = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize();
    const endIndex = startIndex + this.pageSize();
    return this.companies().slice(startIndex, endIndex);
  });

  totalPages = computed(() => Math.ceil(this.companies().length / this.pageSize()));

  ngOnInit() {
    this.companyService.getAllCompaniesLookup().subscribe({
      next: (res) => {
        this.companies.set(res.list.map((c) => ({ id: c.id, titleEn: c.titleEn, titleAr: c.titleAr })));
        res.list.forEach((company: CompanyLookupItem) => {
          if (company.profileImageFid) {
            this.fileMgt.getImageUrl(company.profileImageFid).subscribe({
              next: (data) => this.companyImages.set(company.id, data),
              error: () => this.companyImages.set(company.id, { url: '', isImage: false }),
            });
          }
        });
      },
      error: (err) => {
        console.error('Error fetching companies', err);
        this.errorMessage.set(
          this.isArabic()
            ? 'حدث خطأ أثناء تحميل الشركات، حاول مرة أخرى لاحقًا'
            : 'Failed to load companies. Please try again later.',
        );
      },
    });
  }

  getLocalizedTitle(company: Company): string {
    return this.isArabic() ? company.titleAr || company.titleEn : company.titleEn;
  }

  viewCompany(company: Company) {
    this.router.navigate(['/user/products'], {
      queryParams: {
        mfgCompanyId: company.id,
        mfgCompanyTitleEn: company.titleEn,
        mfgCompanyTitleAr: company.titleAr,
      },
    });
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  get pagesArray() {
    return Array(this.totalPages())
      .fill(0)
      .map((x, i) => i + 1);
  }
}
